// Copyright 2024 Sorama B.V.

import { Disposable } from "aurelia-framework";

/**
 * A subscribable object that can be used to notify subscribers of changes.
 */
export class Subscribable<T> {
    /** The subscriptions that are currently active. */
    private readonly subscriptions: { id: string; callbacks: ((value: T) => void)[] }[] = [];

    /**
     * Subscribes to changes of an object.
     * @param id The object id to subscribe to.
     * @param callback The callback that will be called when the object value changes.
     * @returns A disposable that can be used to unsubscribe from the object.
     */
    public subscribe(id: string, callback: (value: T) => void): Disposable {
        // First find the object if it already exists
        const objectSubscriptions = this.subscriptions.find((s) => s.id === id);
        if (objectSubscriptions) {
            objectSubscriptions.callbacks.push(callback);
        } else {
            // If the object does not exist yet, create it
            this.subscriptions.push({ id, callbacks: [callback] });
        }

        for (const subscription of this.subscriptions) {
            if (subscription.id === id) {
                subscription.callbacks.push(callback);
                return;
            }
        }

        return {
            dispose: () => {
                for (const subscription of this.subscriptions) {
                    if (subscription.id === id) {
                        subscription.callbacks = subscription.callbacks.filter((c) => c !== callback);
                        return;
                    }
                }
            },
        };
    }

    /**
     * Notifies all subscribers of a change in the object.
     * @param id The object id that has changed.
     * @param value The new value of the object.
     */
    public notify(id: string, value: T): void {
        for (const subscription of this.subscriptions) {
            if (subscription.id === id) {
                for (const callback of subscription.callbacks) {
                    callback(value);
                }
            }
        }
    }
}
